import { createContext, useCallback, useMemo, useState } from 'react';
import { FinalDisposition } from '@/common/containers/Modals/index';
import { useToggle } from '@/common/hooks/index';
import FinAssetsModal from '@/common/containers/Modals/FinAssetsModal/index';
import { Notification } from '@/common/utils/index';
import { useTranslation } from 'react-i18next';

export const DetailsContext = createContext({});

const DetailsProvider = ({ refreshTask, section, children }) => {
  const { t } = useTranslation();
  const [intendedPlan, _setIntendedPlan] = useState(null);
  const [assetValues, _setAssetValues] = useState(null);
  const [finalDisposition, setDispositionData] = useState(null);
  const [openEditDisposition, toggleEditDisposition] = useToggle(false);
  const [finalDispositionOptions, setFinalDispositionOptions] = useState({});
  const [finValues, setFinValues] = useState(null);

  const setIntendedPlan = ({ taskStepId, options, answers }) => {
    const answer = answers.length
      ? answers.find(({ status }) => status === 'active')
      : null;

    _setIntendedPlan({
      taskStepId,
      element: options.properties[0] || [],
      value: answer ? answer.value.plan : 'none',
      answerId: answer ? answer.id : null,
    });
  };

  const setAssetValues = ({ taskStepId, options, answers }) => {
    const answer = answers.length
      ? answers.find(({ status }) => status === 'active')
      : null;

    _setAssetValues({
      taskStepId,
      options: options.properties || [],
      value: answer ? answer.value : null,
      answerId: answer ? answer.id : null,
    });
  };

  const closeFinModal = () => {
    setFinValues(null);
  };

  const openFinalDisposition = useCallback(
    (options = {}) => {
      // the task has a final disposition link but no final disposition step
      if (!finalDisposition) {
        Notification.error(t('Something went wrong'));
        return;
      }
      setFinalDispositionOptions(options);
      toggleEditDisposition(true);
    },
    [finalDisposition, t, toggleEditDisposition]
  );

  const closeFinalDisposition = useCallback(() => {
    toggleEditDisposition(false);
    setFinalDispositionOptions({});
  }, [toggleEditDisposition]);

  const contextValue = useMemo(
    () => ({
      intendedPlan,
      setIntendedPlan,
      assetValues,
      setAssetValues,
      finalDisposition,
      setDispositionData,
      toggleEditDisposition,
      openFinalDisposition,
      setFinValues,
      refreshTask,
      section,
    }),
    [
      intendedPlan,
      assetValues,
      finalDisposition,
      openFinalDisposition,
      refreshTask,
      section,
      toggleEditDisposition,
    ]
  );

  return (
    <DetailsContext.Provider value={contextValue}>
      {children}
      {openEditDisposition && !!finalDisposition && (
        <FinalDisposition
          form={finalDisposition}
          onClose={closeFinalDisposition}
          refreshTask={refreshTask}
          {...finalDispositionOptions}
          open
          overlay
        />
      )}
      {!!finValues && (
        <FinAssetsModal
          form={finValues}
          onClose={closeFinModal}
          refreshTask={refreshTask}
          open
          overlay
        />
      )}
    </DetailsContext.Provider>
  );
};

export default DetailsProvider;
